import React, { useState, useRef, useEffect } from 'react';

/**
 * MemoryTest — Module 4 (Grid Recall)
 * A 3x3 grid flashes cells in a sequence. Player must click them back in order.
 * 10 rounds, sequence length ramps 3 → 6.
 * Scored on correctCount / sequenceLength across all rounds.
 */
export default function MemoryTest({ trials, onComplete, icon, moduleNum = 4, totalModules = 6 }) {
  const [uiState, setUiState] = useState('instructions'); // instructions | running | done
  const [highlightCell, setHighlightCell] = useState(null);  // cell index being shown
  const [phase, setPhase] = useState('show');              // show | recall
  const [clickFeedback, setClickFeedback] = useState({});  // { cellIdx: 'correct' | 'wrong' }
  const [roundResult, setRoundResult] = useState(null);    // null | 'correct' | 'wrong'
  const [progress, setProgress] = useState(0);
  const [isWarmupBanner, setIsWarmupBanner] = useState(false);
  const [sequenceLength, setSequenceLength] = useState(0); // for display

  const cancelled = useRef(false);
  const phaseRef = useRef('show');
  const trialIndexRef = useRef(0);
  const clickIndexRef = useRef(0);
  const isWarmupRef = useRef(true);
  const resultsRef = useRef([]);

  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const WARMUP_TRIALS = [
    { sequence: [4, 1, 7] },
    { sequence: [0, 6, 3] },
  ];

  const SHOW_DURATION = 550;  // each cell lit for 550ms
  const SHOW_BLANK    = 220;  // blank between cells during show
  const ROUND_PAUSE   = 900;  // pause after wrong/complete before next round

  // --- Ref-based functions to avoid stale closures ---

  const doNextRoundRef = useRef(null);
  const doShowPhaseRef = useRef(null);

  doShowPhaseRef.current = (sequence) => {
    if (cancelled.current) return;
    phaseRef.current = 'show';
    setPhase('show');
    setHighlightCell(null);
    setClickFeedback({});
    setRoundResult(null);
    setSequenceLength(sequence.length);

    let i = 0;
    const showNext = () => {
      if (cancelled.current) return;
      if (i >= sequence.length) {
        setHighlightCell(null);
        setTimeout(() => {
          if (cancelled.current) return;
          phaseRef.current = 'recall';
          setPhase('recall');
          clickIndexRef.current = 0;
        }, 450);
        return;
      }
      setHighlightCell(sequence[i]);
      i++;
      setTimeout(() => {
        if (cancelled.current) return;
        setHighlightCell(null);
        setTimeout(showNext, SHOW_BLANK);
      }, SHOW_DURATION);
    };
    setTimeout(showNext, 400);
  };

  doNextRoundRef.current = () => {
    if (cancelled.current) return;
    const isWarmup = isWarmupRef.current;
    const activeTrials = isWarmup ? WARMUP_TRIALS : trials;

    trialIndexRef.current++;

    if (trialIndexRef.current >= activeTrials.length) {
      if (isWarmup) {
        isWarmupRef.current = false;
        trialIndexRef.current = 0;
        resultsRef.current = [];
        setIsWarmupBanner(true);
        setTimeout(() => {
          if (cancelled.current) return;
          setIsWarmupBanner(false);
          setProgress(0);
          const firstSeq = trials[0]?.sequence;
          if (firstSeq) doShowPhaseRef.current(firstSeq);
        }, 2000);
      } else {
        setUiState('done');
        onCompleteRef.current(resultsRef.current);
      }
      return;
    }

    const nextTrial = activeTrials[trialIndexRef.current];
    if (!nextTrial?.sequence) return;
    if (!isWarmup) {
      setProgress(Math.round((trialIndexRef.current / trials.length) * 100));
    }
    doShowPhaseRef.current(nextTrial.sequence);
  };

  const handleCellClick = (cellIndex) => {
    if (phaseRef.current !== 'recall') return;

    const isWarmup = isWarmupRef.current;
    const activeTrials = isWarmup ? WARMUP_TRIALS : trials;
    const trial = activeTrials[trialIndexRef.current];
    if (!trial) return;

    const { sequence } = trial;
    const ci = clickIndexRef.current;
    if (ci >= sequence.length) return;

    const isCorrect = cellIndex === sequence[ci];

    if (isCorrect) {
      setClickFeedback((prev) => ({ ...prev, [cellIndex]: 'correct' }));
      clickIndexRef.current++;

      if (clickIndexRef.current >= sequence.length) {
        phaseRef.current = 'done';
        setRoundResult('correct');
        if (!isWarmup) {
          resultsRef.current.push({ type: 'grid_recall', sequenceLength: sequence.length, correctCount: sequence.length, correct: true });
        }
        setTimeout(() => doNextRoundRef.current(), ROUND_PAUSE);
      }
    } else {
      phaseRef.current = 'done';
      setClickFeedback((prev) => ({ ...prev, [cellIndex]: 'wrong' }));
      setRoundResult('wrong');
      if (!isWarmup) {
        resultsRef.current.push({ type: 'grid_recall', sequenceLength: sequence.length, correctCount: ci, correct: false });
      }
      setTimeout(() => doNextRoundRef.current(), ROUND_PAUSE);
    }
  };

  const startTest = () => {
    cancelled.current = false;
    isWarmupRef.current = true;
    trialIndexRef.current = 0;
    clickIndexRef.current = 0;
    resultsRef.current = [];
    setUiState('running');
    setProgress(0);
    setClickFeedback({});
    setRoundResult(null);
    doShowPhaseRef.current(WARMUP_TRIALS[0].sequence);
  };

  useEffect(() => {
    return () => { cancelled.current = true; };
  }, []);

  // --- Cell styling helpers ---
  const getCellStyle = (idx) => {
    const base = {
      width: 72, height: 72, borderRadius: 10,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      cursor: phase === 'recall' ? 'pointer' : 'default',
      transition: 'background 0.1s, border-color 0.1s, transform 0.1s',
      userSelect: 'none', WebkitUserSelect: 'none',
      border: '2px solid var(--border)',
      background: 'var(--bg-secondary)',
    };

    if (highlightCell === idx) {
      return { ...base, background: 'rgba(0,212,255,0.25)', borderColor: '#00d4ff', transform: 'scale(1.06)' };
    }
    if (clickFeedback[idx] === 'correct') {
      return { ...base, background: 'rgba(16,185,129,0.25)', borderColor: '#10b981' };
    }
    if (clickFeedback[idx] === 'wrong') {
      return { ...base, background: 'rgba(239,68,68,0.25)', borderColor: '#ef4444' };
    }
    if (phase === 'recall' && !roundResult) {
      return { ...base, borderColor: 'rgba(0,212,255,0.35)' };
    }
    return base;
  };

  // --- Render ---

  if (uiState === 'instructions') {
    return (
      <div className="card text-center" style={{ maxWidth: 480, margin: '0 auto' }}>
        {icon && (
          <div style={{ width: 56, height: 56, borderRadius: 14, background: 'var(--accent-dim)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)', margin: '0 auto 20px' }}>
            {icon}
          </div>
        )}
        <h2 style={{ marginBottom: 8 }}>Working Memory</h2>
        <p style={{ marginBottom: 16, lineHeight: 1.7 }}>
          A 3×3 grid will light up cells one at a time. Watch the sequence, then click
          the cells back <strong>in the same order</strong>.
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginBottom: 16 }}>
          {[0,1,2,3,4,5,6,7,8].map((i) => (
            <div key={i} style={{
              width: 24, height: 24, borderRadius: 5, border: '1.5px solid var(--border)',
              background: [1,5,7].includes(i) ? 'rgba(0,212,255,0.2)' : 'var(--bg-secondary)',
              borderColor: [1,5,7].includes(i) ? '#00d4ff' : 'var(--border)',
            }} />
          ))}
        </div>
        <p className="text-muted" style={{ fontSize: 13, marginBottom: 8 }}>
          Sequences start at 3 cells and grow to 6. You get 10 rounds total.
        </p>
        <p className="text-muted" style={{ fontSize: 12, marginBottom: 24, lineHeight: 1.6 }}>
          <span style={{ color: '#10b981' }}>Green</span> = correct position.{' '}
          <span style={{ color: '#ef4444' }}>Red</span> = wrong — round ends there.
          A short warmup round comes first.
        </p>
        <button className="btn btn--primary" onClick={startTest}>Start</button>
      </div>
    );
  }

  const isWarmup = isWarmupRef.current && !isWarmupBanner;

  return (
    <div style={{ width: '100%', maxWidth: 440, margin: '0 auto' }}>
      {/* Progress */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <div className="progress-bar" style={{ flex: 1 }}>
          <div className="progress-bar__fill" style={{ width: `${progress}%` }} />
        </div>
        <span style={{ fontSize: 13, whiteSpace: 'nowrap', color: isWarmup ? '#f59e0b' : 'var(--text-muted)' }}>
          {isWarmup ? 'PRACTICE — not scored' : `${progress}%`}
        </span>
      </div>

      <div style={{ position: 'relative' }}>
        <div className="card" style={{ padding: '32px 24px', textAlign: 'center' }}>

          {/* Phase label */}
          <div style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 20 }}>
            {phase === 'show' ? `Watch — ${sequenceLength} cells` : roundResult ? (roundResult === 'correct' ? '✓ Complete!' : '✗ Wrong order') : 'Click in order'}
          </div>

          {/* Grid */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(3, 72px)',
            gap: 10, margin: '0 auto 24px', width: 'fit-content',
          }}>
            {Array.from({ length: 9 }, (_, i) => (
              <div
                key={i}
                style={getCellStyle(i)}
                onPointerDown={(e) => { e.preventDefault(); handleCellClick(i); }}
              />
            ))}
          </div>

          {/* Result message */}
          <div style={{ height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {roundResult && (
              <span style={{ fontSize: 13, fontWeight: 700, color: roundResult === 'correct' ? '#10b981' : '#ef4444' }}>
                {roundResult === 'correct' ? 'Perfect sequence!' : 'Sequence broken — next round shortly'}
              </span>
            )}
          </div>
        </div>

        {/* Warmup complete overlay */}
        {isWarmupBanner && (
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(10,14,26,0.93)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            borderRadius: 8,
          }}>
            <div style={{ fontSize: 40, marginBottom: 10, color: '#10b981' }}>✓</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#10b981', marginBottom: 8 }}>Warmup Complete!</div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)' }}>Starting scored rounds in 2 seconds…</div>
          </div>
        )}
      </div>

      <p className="text-center text-muted" style={{ fontSize: 13, marginTop: 12 }}>
        Module {moduleNum} of {totalModules} — Working Memory
      </p>
    </div>
  );
}
